import {
  Box,
  Grid,
  InputLabel,
  type InputLabelProps,
  Typography,
} from "@mui/material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { ReactElement } from "react";
import {
  Control,
  FieldValues,
  Path,
  useController,
  UseControllerProps,
} from "react-hook-form";

import { FlexBox } from "~frontend/components/FlexBox";
import { palette } from "~frontend/theme";
import { timezoneDisplayName } from "~frontend/util/dateFormat";

// Rounds up to the next 30-minute mark (00 or 30), keeping it at/after `base`.
function roundUpToHalfHour(base: DateTime): DateTime {
  const remainder = base.minute % 30;
  const minutesToAdd = remainder === 0 ? 0 : 30 - remainder;
  return base
    .plus({ minutes: minutesToAdd })
    .set({ second: 0, millisecond: 0 });
}

function Label({
  optional,
  children,
  ...rest
}: { optional?: boolean } & InputLabelProps) {
  return (
    <InputLabel shrink {...rest}>
      <FlexBox alignItems="center" gap={1}>
        {children}
        {optional && <Typography>(optional)</Typography>}
      </FlexBox>
    </InputLabel>
  );
}

export function RhfDateTimePicker<T extends FieldValues>({
  name,
  dateLabel,
  timeLabel,
  optional = false,
  variant = "outlined",
  disabled = false,
  minDate,
  minTime,
  inputLabelSlotProps,
  control,
  timezone,
  rules,
}: {
  name: Path<T>;
  dateLabel: string;
  timeLabel: string;
  optional?: boolean;
  variant?: "outlined" | "filled" | "standard";
  disabled?: boolean;
  minDate?: DateTime;
  minTime?: DateTime;
  inputLabelSlotProps?: InputLabelProps;
  control: Control<T>;
  timezone?: string;
  rules?: UseControllerProps<T>["rules"];
}): ReactElement {
  const {
    field,
    fieldState,
    formState: { submitCount },
  } = useController({
    control,
    name,
    rules,
  });

  const value = (() => {
    const raw = field.value as unknown;
    let parsed: DateTime | null = null;
    if (!raw) {
      return null;
    }
    if (typeof raw === "string") {
      parsed = DateTime.fromISO(raw);
    } else if (typeof raw === "number") {
      // When a DateTimePicker is rendered using data from the api, the
      // timestamp comes back in epoch time as opposed to a string.
      parsed = DateTime.fromMillis(raw);
    } else if (raw instanceof Date) {
      parsed = DateTime.fromJSDate(raw);
    }
    if (!parsed) {
      return null;
    }
    // Display the stored instant in the requested zone so the wall-clock the
    // user sees/edits matches that timezone.
    return timezone ? parsed.setZone(timezone) : parsed;
  })();

  // Store as a UTC ISO string regardless of the display zone.
  const commit = (dateTime: DateTime) =>
    field.onChange(dateTime.toUTC().toISO());
  const nowInZone = () =>
    timezone ? DateTime.now().setZone(timezone) : DateTime.now();

  function handleDateChange(newDate: DateTime | null) {
    // Picking from the calendar fires onChange but not the input's blur, so
    // mark the field touched here so its error state can surface.
    field.onBlur();
    if (!newDate) {
      field.onChange(null);
      return;
    }
    if (!newDate.isValid) {
      return;
    }
    // Keep the existing time, or default to the next 30-minute mark from now
    // (in the picker's zone) rather than midnight, so it isn't in the past.
    const time = value?.isValid ? value : roundUpToHalfHour(nowInZone());
    commit(
      newDate.set({
        hour: time.hour,
        minute: time.minute,
        second: 0,
        millisecond: 0,
      })
    );
  }

  function handleTimeChange(newTime: DateTime | null) {
    // Selecting from the clock fires onChange but not the input's blur, so
    // mark the field touched here so its error state can surface.
    field.onBlur();
    if (!newTime) {
      if (optional) {
        field.onChange(null);
      }
      return;
    }
    if (!newTime.isValid) {
      return;
    }
    // Keep the existing date, or use today's date (in the picker's zone).
    const date = value?.isValid ? value : nowInZone();
    commit(
      date.set({
        hour: newTime.hour,
        minute: newTime.minute,
        second: 0,
        millisecond: 0,
      })
    );
  }

  const isOutlined = variant === "outlined";

  const pickerSx = {
    width: "100%",
    mb: 0,
    ...(isOutlined
      ? {
          "& .MuiPickersInputBase-root": {
            borderRadius: 0,
            backgroundColor: palette.white,
          },
        }
      : {}),
  };

  const showErrorOutline =
    !!fieldState.error && (fieldState.isTouched || submitCount > 0);

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid size={{ xs: 6 }}>
        <Box>
          {dateLabel ? (
            <Label optional={optional} {...inputLabelSlotProps}>
              {dateLabel}
            </Label>
          ) : null}
          <DatePicker
            value={value}
            onChange={handleDateChange}
            disabled={disabled}
            minDate={minDate}
            timezone={timezone}
            slotProps={{
              textField: {
                variant,
                error: showErrorOutline,
                helperText: showErrorOutline
                  ? fieldState.error?.message
                  : undefined,
                onBlur: field.onBlur,
                sx: { ...pickerSx, pt: dateLabel ? 0 : undefined },
              },
            }}
          />
        </Box>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Box>
          {timeLabel ? (
            <Label optional={optional} {...inputLabelSlotProps}>
              {timezone
                ? `${timeLabel} (${timezoneDisplayName(timezone)})`
                : timeLabel}
            </Label>
          ) : null}
          <TimePicker
            value={value}
            onChange={handleTimeChange}
            disabled={disabled}
            minTime={minTime}
            timezone={timezone}
            slotProps={{
              textField: {
                variant,
                error: showErrorOutline,
                onBlur: field.onBlur,
                sx: { ...pickerSx, pt: timeLabel ? 0 : undefined },
              },
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
}
